import { Box, Typography, Fade, Paper } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AutoStoriesIcon from '@mui/icons-material/AutoStories';

const RelatedBooks = ({ books, orientation = "row" }) => {
  const navigate = useNavigate();
  const [language] = useState(
    localStorage.getItem("currentLanguage")?.toUpperCase() || "EN"
  );

  if (!books || books.length === 0) {
    return (
      <Box sx={{ width: '100%', textAlign: 'center', py: 4, opacity: 0.6 }}>
        <AutoStoriesIcon sx={{ fontSize: 40, mb: 1, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">
          No related books found.
        </Typography>
      </Box>
    );
  }

  const placeholderImg = "/placeholder-book.svg";

  return (
    <Box 
      sx={{ 
        display: "grid", 
        gridTemplateColumns: orientation === "column" ? "1fr" : {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)"
        },
        gap: 3,
        mt: 2
      }}
    >
      {books.map((book, index) => (
        <Fade in={true} timeout={300 + (index * 100)} key={`related-book-${book.UPLOAD_ID || book.ID || index}`}>
          <Paper
            elevation={1}
            sx={{
              display: "flex",
              flexDirection: "row",
              height: orientation === "column" ? 140 : 180,
              bgcolor: "background.paper",
              borderRadius: 2,
              overflow: "hidden",
              transform: "translateY(0)",
              transition: "all 0.2s ease !important",
              border: "1px solid",
              borderColor: "divider",
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                "& .cover-image": {
                  transform: "scale(1.05)",
                }
              }
            }}
            onClick={() => {
              navigate("/books/details", { state: { book: { ...book } } });
            }}
          >
            {/* Left side: Cover Image */}
            <Box sx={{ width: "30%", height: "100%", overflow: "hidden", position: "relative" }}>
              <Box
                className="cover-image"
                component="img"
                src={book.COVER_IMAGE || placeholderImg}
                onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
                alt={book[`TITLE_${language}`] || book.TITLE_EN}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transition: "transform 0.4s ease",
                }}
              />
            </Box>

            {/* Right side: Content */}
            <Box sx={{ 
              width: "70%", 
              p: 1.5, 
              display: "flex", 
              flexDirection: "column",
            }}>
              <Typography 
                variant="subtitle2" 
                fontWeight="700" 
                sx={{ 
                  lineHeight: 1.2,
                  mb: 0.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden"
                }}
              >
                {book[`TITLE_${language}`] || book.TITLE_EN}
              </Typography>
              
              <Typography 
                variant="caption" 
                fontWeight="600"
                sx={{ 
                  mb: 1, 
                  display: "inline-block", 
                  color: "text.secondary",
                  alignSelf: "flex-start"
                }}
              >
                {book.AUTHOR_NAME || "Unknown"}
              </Typography>

              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: orientation === "column" ? 2 : 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  fontSize: "0.8rem",
                  lineHeight: 1.4,
                  opacity: 0.8
                }}
              >
                {book[`SUMMARY_${language}`] || book.SUMMARY_EN || book[`DESCRIPTION_${language}`] || book.DESCRIPTION_EN || "No summary available."}
              </Typography>
            </Box>
          </Paper>
        </Fade>
      ))}
    </Box>
  );
};

export default RelatedBooks;
